import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutGrid, Smartphone, Shirt, Sofa, Home as HomeIcon, Car, BookOpen,
  Dumbbell, UtensilsCrossed, Leaf, Ticket, Wrench, Sparkles,
  Package, Gift, Music, Gamepad2, Camera, Watch,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  Smartphone, Shirt, Sofa, Home: HomeIcon, Car, BookOpen, Dumbbell,
  UtensilsCrossed, Leaf, Wrench, Sparkles, Ticket, Package, Gift,
  Music, Gamepad2, Camera, Watch,
};

// Routes where the chip bar should NOT render
const HIDDEN_PATHS = ['/', '/auth', '/forgot-password'];
const HIDDEN_PREFIXES = ['/admin', '/chat'];

export default function CategoryChipBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [primaryColor, setPrimaryColor] = useState('#4F46E5');

  // Read currently-active category from URL ?category=X (only meaningful on /)
  const params = new URLSearchParams(location.search);
  const activeCategory = params.get('category') || '';

  useEffect(() => {
    let mounted = true;
    Promise.all([
      axios.get(`${API_URL}/api/categories`).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/settings/public`).catch(() => ({ data: {} })),
    ]).then(([catsRes, settingsRes]) => {
      if (!mounted) return;
      if (Array.isArray(catsRes.data)) setCategories(catsRes.data);
      if (settingsRes.data?.theme_primary_color) setPrimaryColor(settingsRes.data.theme_primary_color);
    });
    return () => { mounted = false; };
  }, []);

  // Hide on excluded routes
  const path = location.pathname;
  const hidden =
    HIDDEN_PATHS.includes(path) ||
    HIDDEN_PREFIXES.some((p) => path.startsWith(p));
  if (hidden) return null;

  // Nothing to show if categories haven't loaded
  if (categories.length === 0) return null;

  const handleClick = (categoryName) => {
    // Navigate to home with category query param. HomePage will sync state from URL.
    if (!categoryName) {
      navigate('/');
    } else {
      navigate(`/?category=${encodeURIComponent(categoryName)}`);
    }
  };

  return (
    <div
      className="sticky top-[64px] z-30 bg-white/95 backdrop-blur-md border-b border-slate-200"
      data-testid="global-category-chip-bar"
    >
      <div className="max-w-7xl mx-auto px-3 md:px-4 py-2.5">
        <div
          className="flex items-center gap-2 overflow-x-auto"
          style={{ scrollbarWidth: 'thin' }}
        >
          <button
            onClick={() => handleClick('')}
            data-testid="global-chip-all-categories"
            className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all whitespace-nowrap ${
              activeCategory === ''
                ? 'text-white border-transparent shadow-md'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
            }`}
            style={activeCategory === '' ? { background: primaryColor } : {}}
          >
            <LayoutGrid className="w-3.5 h-3.5" />
            <span>All Categories</span>
          </button>
          {categories.map((cat) => {
            const Icon = ICON_MAP[cat.icon_name] || Sparkles;
            const active = activeCategory === cat.name;
            return (
              <button
                key={cat.id || cat.name}
                onClick={() => handleClick(cat.name)}
                data-testid={`global-chip-${cat.name}`}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all whitespace-nowrap ${
                  active
                    ? 'text-white border-transparent shadow-md'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
                style={active ? { background: primaryColor } : {}}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{cat.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
